import { CheckCircle2, Mail, PlugZap, RefreshCw, Undo2, XCircle } from 'lucide-react'
import { useState } from 'react'

import { useIsAdmin } from '../api/auth'
import {
  useBlockedNumbers,
  useEmails,
  useReparseEmail,
  useSyncStatus,
  useTriggerSync,
  useUnblockNumber,
} from '../api/client'
import { secondaryButton } from '../components/AddParcelDialog'
import { de } from '../i18n/de'
import { formatDate, formatDateTime, formatRelative } from '../lib/format'

/**
 * Operational transparency: what is configured, what ran last, and which mails the parsers
 * did not understand. The unparsed list is the backlog for improving parsing rather than a
 * silent failure.
 */
export function SyncStatus() {
  const { data: status, isLoading } = useSyncStatus()
  const { data: emails } = useEmails('no_match')
  const triggerSync = useTriggerSync()
  const reparse = useReparseEmail()
  const [note, setNote] = useState<string | null>(null)
  // Eigene Meldung in der Liste: die allgemeine steht weit oben, außer Sicht, wenn man hier
  // unten auf "Erneut auswerten" klickt.
  const [reparseNote, setReparseNote] = useState<string | null>(null)
  const isAdmin = useIsAdmin()
  const { data: blocked } = useBlockedNumbers(isAdmin)
  const unblock = useUnblockNumber()

  if (isLoading || !status) {
    return <p className="px-4 py-6 text-sm text-slate-400">…</p>
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
      <h1 className="text-lg font-semibold">{de.sync.title}</h1>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card title={`${de.sync.imap} · ${status.mail_backend_label}`}>
          <Indicator
            ok={status.mail_configured}
            okText={status.mail_target ?? de.sync.imapConfigured}
            failText={de.sync.imapMissing}
          />
          <p className="mt-2 text-xs text-slate-500">
            {de.sync.lastImap}:{' '}
            {status.last_imap_run
              ? formatRelative(status.last_imap_run.started_at)
              : de.sync.never}
          </p>
          {status.last_imap_run?.error && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
              {status.last_imap_run.error}
            </p>
          )}
          <button
            onClick={() =>
              triggerSync.mutate('emails', {
                onSuccess: (result) => setNote(result.message),
                onError: (error) => setNote((error as Error).message),
              })
            }
            disabled={!status.mail_configured || triggerSync.isPending}
            className={secondaryButton + ' mt-3 inline-flex items-center gap-1.5'}
          >
            <Mail className="h-3.5 w-3.5" aria-hidden />
            {de.actions.syncEmails}
          </button>
          <button
            onClick={() => {
              void fetch('/api/sync/mail-check')
                .then((r) => r.json())
                .then((r) => setNote(r.message))
            }}
            className={secondaryButton + ' mt-3 ml-2 inline-flex items-center gap-1.5'}
          >
            <PlugZap className="h-3.5 w-3.5" aria-hidden />
            {de.sync.mailCheck}
          </button>
        </Card>

        <Card title={de.sync.scheduler}>
          <Indicator
            ok={status.scheduler_enabled}
            okText="aktiv"
            failText="deaktiviert"
          />
          <p className="mt-2 text-xs text-slate-500">
            {de.sync.pollWindow}:{' '}
            {status.within_poll_window ? de.sync.inWindow : de.sync.outOfWindow}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {de.sync.lastPoll}:{' '}
            {status.last_poll_run
              ? formatRelative(status.last_poll_run.started_at)
              : de.sync.never}
          </p>
          <button
            onClick={() =>
              triggerSync.mutate('carriers', {
                onSuccess: (result) => setNote(result.message),
                onError: (error) => setNote((error as Error).message),
              })
            }
            disabled={triggerSync.isPending}
            className={secondaryButton + ' mt-3 inline-flex items-center gap-1.5'}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${triggerSync.isPending ? 'animate-spin' : ''}`}
              aria-hidden
            />
            {de.actions.syncCarriers}
          </button>
        </Card>
      </div>

      {note && (
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm dark:bg-slate-800">{note}</p>
      )}

      <Card title={de.sync.carriers}>
        <p className="mb-3 text-xs text-slate-500">{de.sync.apiHint}</p>
        <ul className="space-y-2">
          {status.carriers.map((carrier) => (
            <li key={carrier.code} className="flex items-center gap-3 text-sm">
              <span className="w-28 shrink-0 font-medium">{carrier.name}</span>
              <Indicator
                ok={carrier.configured}
                okText={de.sync.configured}
                failText={de.sync.notConfigured}
              />
              {carrier.configured && (
                <span
                  className="ml-auto text-xs text-slate-500 tabular-nums"
                  title={[
                    de.sync.budgetHint,
                    carrier.budget_group && carrier.budget_group !== carrier.code
                      ? `Gemeinsames Kontingent: ${carrier.budget_group}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join('\n')}
                >
                  {de.sync.budget}:{' '}
                  {de.sync.budgetLeft(carrier.budget_remaining, carrier.budget_total)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card title={`${de.sync.unparsed} (${status.unparsed_email_count})`}>
        <p className="mb-3 text-xs text-slate-500">{de.sync.unparsedHint}</p>
        {reparseNote && (
          <p className="mb-3 rounded-lg bg-slate-100 px-3 py-2 text-sm dark:bg-slate-800">
            {reparseNote}
          </p>
        )}
        {emails?.items.length ? (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {emails.items.map((email) => (
              <li key={email.id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{email.subject ?? '(kein Betreff)'}</p>
                  <p className="truncate text-xs text-slate-500">
                    {email.from_addr} · {formatDateTime(email.received_at)}
                  </p>
                  {email.error && (
                    <p
                      className="truncate text-xs text-amber-700 dark:text-amber-400"
                      title={email.error}
                    >
                      {de.sync.unparsedReason}: {email.error}
                    </p>
                  )}
                </div>
                {email.content_purged_at ? (
                  <span
                    className="shrink-0 text-xs text-slate-400"
                    title={de.sync.contentPurged(formatDate(email.content_purged_at))}
                  >
                    {de.sync.contentPurgedShort}
                  </span>
                ) : (
                  <button
                    onClick={() =>
                      reparse.mutate(email.id, {
                        onSuccess: (result) => setReparseNote(result.message),
                        onError: (error) => setReparseNote((error as Error).message),
                      })
                    }
                    disabled={reparse.isPending}
                    className={secondaryButton + ' shrink-0 text-xs'}
                  >
                    {de.actions.reparse}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">–</p>
        )}
      </Card>

      {/* Only administrators can delete a parcel, so only they can see or lift the blocks
          that deleting leaves behind. */}
      {isAdmin && (
        <Card title={`${de.sync.blockedNumbers} (${blocked?.length ?? 0})`}>
          <p className="mb-3 text-xs text-slate-500">{de.sync.blockedHint}</p>
          {blocked?.length ? (
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {blocked.map((entry) => (
                <li key={entry.id} className="flex items-center gap-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-xs">{entry.tracking_number}</p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {[entry.carrier_code, entry.description].filter(Boolean).join(' · ')}
                      {entry.description || entry.carrier_code ? ' · ' : ''}
                      {de.sync.blockedBy(
                        entry.blocked_by ?? '–',
                        formatDateTime(entry.blocked_at),
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => unblock.mutate(entry.tracking_number)}
                    disabled={unblock.isPending}
                    className={secondaryButton + ' inline-flex shrink-0 items-center gap-1.5'}
                  >
                    <Undo2 className="h-3.5 w-3.5" aria-hidden />
                    {de.actions.unblock}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">{de.sync.blockedEmpty}</p>
          )}
        </Card>
      )}
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <h2 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {title}
      </h2>
      {children}
    </section>
  )
}

function Indicator({
  ok,
  okText,
  failText,
}: {
  ok: boolean
  okText: string
  failText: string
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm ${
        ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
      }`}
    >
      {ok ? (
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
      ) : (
        <XCircle className="h-4 w-4 shrink-0" aria-hidden />
      )}
      {ok ? okText : failText}
    </span>
  )
}
